import React, { useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { classNames } from 'primereact/utils';
import { CustomValidationMessage } from '../../../Components/CustomMessage';
import { WIZARD_TYPES } from '../../../constants/planWizardTypes';
import {
  AutoBillingField,
  CostField,
  FixedFlexibleRadios,
  SetupFeeFields,
  TargetPartsFields,
  toInputNumberValue,
} from './sharedFields';

const AmountStep = (props) => {
  const [deductionError, setDeductionError] = useState(null);
  const {
    wizardType,
    formik,
    t,
    Trans,
    business,
    period,
    supportsAutoBilling,
    onSelectFixed,
    onSelectFlexible,
    onChangeBillingPeriod,
    onChangeFeeCheck,
    equalParts,
    varying,
    onChangeEqualParts,
    onChangeVarying,
    recoverMissed,
    notRecover,
    onChangeRecover,
    onChangeNotRecover,
    getBillingPeriodMax,
    isFormFieldValid,
    getFormErrorMessage,
    isFormFieldValidCost,
    getFormErrorMessageCost,
    getFormErrorMessageBilling,
    isFormFieldValidDeduction,
    getFormErrorMessageDeduction,
    isFormFieldValidSetupFee,
    getFormErrorMessageSetupFee,
    amountCurrency,
  } = props;

  const businessPurpose = business?.purpose;
  const policyCode = formik.values.policy?.code;

  const billingPeriodIsOnce = () => formik.values.billingPeriod?.code === 'ONCE';

  const deductionMaxError = () => {
    const max = getBillingPeriodMax();
    const key = businessPurpose === 'SERVICES'
      ? 'products.deduction_error_payments_max'
      : 'products.deduction_error_donations_max';
    return <CustomValidationMessage severity="error" style={{ height: '1.5rem' }} text={t(key, { max })} />;
  };

  const deductionMinError = () => {
    const key = businessPurpose === 'SERVICES'
      ? 'products.deduction_error_payments_min'
      : 'products.deduction_error_donations_min';
    return <CustomValidationMessage severity="error" style={{ height: '1.5rem' }} text={t(key, { min: 2 })} />;
  };

  const renderSingle = () => (
    <>
      <FixedFlexibleRadios
        formik={formik}
        t={t}
        onSelectFixed={onSelectFixed}
        onSelectFlexible={onSelectFlexible}
      />
      <div className="plan-wizard-field-error">
        {getFormErrorMessage('policy')}
      </div>
      {policyCode === 'FIXED' && (
        <CostField
          formik={formik}
          t={t}
          Trans={Trans}
          business={business}
          amountCurrency={amountCurrency}
          isFormFieldValidCost={isFormFieldValidCost}
          getFormErrorMessageCost={getFormErrorMessageCost}
        />
      )}
    </>
  );

  const renderSubscription = () => (
    <>
      <FixedFlexibleRadios
        formik={formik}
        t={t}
        onSelectFixed={onSelectFixed}
        onSelectFlexible={onSelectFlexible}
      />
      <div className="plan-wizard-field-error">
        {getFormErrorMessage('policy')}
      </div>
      {policyCode === 'FIXED' && (
        <CostField
          formik={formik}
          t={t}
          Trans={Trans}
          business={business}
          amountCurrency={amountCurrency}
          isFormFieldValidCost={isFormFieldValidCost}
          getFormErrorMessageCost={getFormErrorMessageCost}
        />
      )}
      <SetupFeeFields
        formik={formik}
        t={t}
        business={business}
        wizardType={wizardType}
        onChangeFeeCheck={onChangeFeeCheck}
        isFormFieldValidSetupFee={isFormFieldValidSetupFee}
        getFormErrorMessageSetupFee={getFormErrorMessageSetupFee}
        showForNonFixed
      />
      <AutoBillingField formik={formik} t={t} supportsAutoBilling={supportsAutoBilling} policyCode={policyCode} />
      {renderPeriodAndDeductions()}
    </>
  );

  const renderCustom = () => (
    <>
      <div className="plan-wizard-field-section plan-wizard-custom-amount-intro">
        <p className="light_hints plan-wizard-custom-amount-intro-lead">{t('plan.subscription_first_hint')}</p>
        <p className="light_hints">{t('plan.subscription_second_hint')}</p>
      </div>
      <SetupFeeFields
        formik={formik}
        t={t}
        business={business}
        wizardType={wizardType}
        onChangeFeeCheck={onChangeFeeCheck}
        isFormFieldValidSetupFee={isFormFieldValidSetupFee}
        getFormErrorMessageSetupFee={getFormErrorMessageSetupFee}
        showForNonFixed
      />
      <AutoBillingField
        formik={formik}
        t={t}
        supportsAutoBilling={supportsAutoBilling}
        policyCode={policyCode || 'CUSTOM'}
      />
      {renderPeriodAndDeductions()}
    </>
  );

  const renderTarget = () => (
    <>
      <TargetPartsFields
        t={t}
        equalParts={equalParts}
        varying={varying}
        onChangeEqualParts={onChangeEqualParts}
        onChangeVarying={onChangeVarying}
        recoverMissed={recoverMissed}
        notRecover={notRecover}
        onChangeRecover={onChangeRecover}
        onChangeNotRecover={onChangeNotRecover}
      />
      {renderPeriodAndDeductions(true)}
    </>
  );

  function renderPeriodAndDeductions(isTarget = false) {
    return (
      <>
        <div className="plan-wizard-field-section">
          <label htmlFor="billingPeriod" className={classNames({ 'p-error': isFormFieldValid('billingPeriod') })}>
            <span className="labels">{t('plan.billing_period')}</span>
            <span className="isterik">*</span>
          </label>
          <div className="inputFields plan-wizard-input">
            <Dropdown
              id="billingPeriod"
              name="billingPeriod"
              value={formik.values.billingPeriod}
              onChange={onChangeBillingPeriod}
              options={period}
              optionLabel="Period"
              placeholder={t('plan.choose_billing_period')}
              className={classNames({ 'p-invalid': isFormFieldValid('billingPeriod') })}
            />
          </div>
          <div className="plan-wizard-field-error">
            {getFormErrorMessageBilling('billingPeriod')}
          </div>
        </div>
        <div className="plan-wizard-field-section">
          <label htmlFor="deductions" className={classNames({ 'p-error': isFormFieldValidDeduction('deductions') })}>
            <span className="labels">
              {businessPurpose === 'SERVICES' ? t('plan.num_payments') : t('plan.num_donations')}
            </span>
            {isTarget && equalParts && <span className="isterik">*</span>}
          </label>
          <div className="inputFields plan-wizard-input">
            <InputNumber
              id="deductions"
              name="deductions"
              value={toInputNumberValue(formik.values.deductions)}
              useGrouping={false}
              disabled={billingPeriodIsOnce()}
              className={classNames({
                'p-invalid': isFormFieldValidDeduction('deductions') || Boolean(deductionError),
              })}
              placeholder={
                businessPurpose === 'SERVICES'
                  ? t('plan.deductions_placeholder_payments')
                  : t('plan.deductions_placeholder_donations')
              }
              onChange={(e) => {
                const value = e.value;
                formik.setFieldTouched('deductions', true, false);
                formik.setFieldValue('deductions', value ?? null);
                const min = 2;
                const max = getBillingPeriodMax();
                const billingPeriodCode = formik.values.billingPeriod?.code;
                let error = null;
                if (value !== null && value !== '' && billingPeriodCode !== 'ONCE') {
                  if (value < min) error = deductionMinError();
                  else if (value > max) error = deductionMaxError();
                }
                if (error) {
                  formik.setFieldError('deductions', error);
                } else {
                  formik.setFieldError('deductions', undefined);
                }
                setDeductionError(error);
              }}
            />
          </div>
          <div className="plan-wizard-field-error">
            {deductionError || getFormErrorMessageDeduction('deductions')}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="wizard-forms-wrapper p-grid p-nogutter">
      <div className="col-12 wizard-forms">
        {wizardType === WIZARD_TYPES.SINGLE && renderSingle()}
        {wizardType === WIZARD_TYPES.SUBSCRIPTION && renderSubscription()}
        {wizardType === WIZARD_TYPES.CUSTOM && renderCustom()}
        {wizardType === WIZARD_TYPES.TARGET && renderTarget()}
      </div>
    </div>
  );
};

export default AmountStep;
